import apiClient, { PaginatedResponse } from './client';

export type RentalCarAvailabilityStatus = 'available' | 'rented' | 'maintenance' | 'inactive';
export type RentalApprovalStatus = 'draft' | 'pending' | 'approved' | 'rejected';
export type RentalBookingStatus = 'pending' | 'confirmed' | 'active' | 'completed' | 'cancelled' | 'rejected';

export interface RentalCarImage {
    id: number;
    image?: string;
    image_url?: string;
    caption?: string;
    sort_order?: number;
}

export interface RentalCarDocument {
    id: number;
    document_type: string;
    file?: string;
    file_url?: string;
    notes?: string;
    expires_at?: string | null;
}

export interface RentalCar {
    id: number;
    vendor_store?: number;
    vendor_store_name?: string;
    vendor_name?: string;
    title: string;
    title_ar?: string;
    make?: number | null;
    make_code?: string;
    make_name?: string;
    model?: number | null;
    model_code?: string;
    model_name?: string;
    car_type?: number | null;
    car_type_code?: string;
    car_type_name?: string;
    year?: number | null;
    seats?: number;
    transmission?: string;
    fuel_type?: string;
    color?: string;
    plate_number?: string;
    vin?: string;
    city?: string;
    address?: string;
    latitude?: number | null;
    longitude?: number | null;
    short_description?: string;
    description?: string;
    tags?: string[];
    features?: string[];
    daily_price: string | number;
    weekly_price?: string | number | null;
    monthly_price?: string | number | null;
    security_deposit?: string | number;
    mileage_limit_per_day?: number | null;
    availability_status: RentalCarAvailabilityStatus;
    is_active: boolean;
    approval_status: RentalApprovalStatus;
    rejection_reason?: string;
    is_featured?: boolean;
    images?: RentalCarImage[];
    documents?: RentalCarDocument[];
    primary_image_url?: string | null;
    is_favorited?: boolean;
    created_at?: string;
    updated_at?: string;
}

export interface RentalBookingAttachment {
    id: number;
    attachment_type: string;
    file?: string;
    file_url?: string;
    note?: string;
    created_at?: string;
}

export interface RentalBooking {
    id: number;
    booking_number: string;
    rental_car: number;
    rental_car_title?: string;
    rental_car_primary_image?: string | null;
    vendor_store: number;
    vendor_store_name?: string;
    customer: number;
    customer_name?: string;
    start_date: string;
    end_date: string;
    total_days: number;
    price_per_day: string | number;
    subtotal: string | number;
    discount_amount?: string | number;
    total_amount: string | number;
    security_deposit?: string | number;
    payment_method?: 'cash' | 'card' | 'bank';
    payment_status?: 'pending' | 'partial' | 'paid' | 'refunded' | string;
    advance_amount?: string | number;
    amount_paid?: string | number;
    amount_remaining?: string | number;
    pickup_location?: string;
    return_location?: string;
    notes?: string;
    vendor_notes?: string;
    cancellation_reason?: string;
    pickup_condition_notes?: string;
    return_condition_notes?: string;
    pickup_mileage?: number | null;
    return_mileage?: number | null;
    status: RentalBookingStatus;
    confirmed_at?: string | null;
    active_at?: string | null;
    completed_at?: string | null;
    cancelled_at?: string | null;
    attachments?: RentalBookingAttachment[];
    created_at?: string;
    updated_at?: string;
}

export interface RentalCarsFilter {
    search?: string;
    brand?: string;
    make?: string;
    model?: string;
    type?: string;
    city?: string;
    location?: string;
    price_min?: number;
    price_max?: number;
    start_date?: string;
    end_date?: string;
    scope?: 'mine' | 'all';
    ordering?: string;
    page?: number;
}

export const rentalsApi = {
    getCars: async (filters?: RentalCarsFilter) => {
        const { data } = await apiClient.get<PaginatedResponse<RentalCar>>('/rentals/cars/', {
            params: filters,
            skipAuth: true,
        } as any);
        return data;
    },

    getCar: async (id: number | string) => {
        const { data } = await apiClient.get<RentalCar>(`/rentals/cars/${id}/`, {
            skipAuth: true,
        } as any);
        return data;
    },

    createCar: async (payload: Partial<RentalCar> | FormData) => {
        const isFormData = payload instanceof FormData;
        const { data } = await apiClient.post<RentalCar>(
            '/rentals/cars/',
            payload,
            isFormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : undefined
        );
        return data;
    },

    updateCar: async (id: number | string, payload: Partial<RentalCar> | FormData) => {
        const isFormData = payload instanceof FormData;
        const { data } = await apiClient.patch<RentalCar>(
            `/rentals/cars/${id}/`,
            payload,
            isFormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : undefined
        );
        return data;
    },

    deleteCar: async (id: number | string) => {
        const { data } = await apiClient.delete(`/rentals/cars/${id}/`);
        return data;
    },

    getCarAvailability: async (id: number | string, start_date: string, end_date: string) => {
        const { data } = await apiClient.get(`/rentals/cars/${id}/availability/`, {
            params: { start_date, end_date },
            skipAuth: true,
        } as any);
        return data;
    },

    getBlockedDates: async (id: number | string) => {
        const { data } = await apiClient.get(`/rentals/cars/${id}/blocked_dates/`);
        return data;
    },

    createBlockedDate: async (id: number | string, payload: { start_date: string; end_date: string; reason?: string }) => {
        const { data } = await apiClient.post(`/rentals/cars/${id}/blocked_dates/`, payload);
        return data;
    },

    deleteBlockedDate: async (id: number | string, blocked_date_id: number) => {
        const { data } = await apiClient.delete(`/rentals/cars/${id}/blocked_dates/`, { data: { blocked_date_id } });
        return data;
    },

    toggleFavorite: async (id: number | string) => {
        const { data } = await apiClient.post(`/rentals/cars/${id}/favorite/`);
        return data;
    },

    getMyBookings: async (params?: object) => {
        const { data } = await apiClient.get<PaginatedResponse<RentalBooking>>('/rentals/bookings/', { params });
        return data;
    },

    createBooking: async (payload: {
        rental_car: number;
        start_date: string;
        end_date: string;
        pickup_location?: string;
        return_location?: string;
        notes?: string;
        payment_method?: 'cash' | 'card' | 'bank';
        advance_amount?: string | number;
    }) => {
        const { data } = await apiClient.post<RentalBooking>('/rentals/bookings/', payload);
        return data;
    },

    setBookingStatus: async (id: number | string, payload: { status: RentalBookingStatus; vendor_notes?: string; cancellation_reason?: string }) => {
        const { data } = await apiClient.post<RentalBooking>(`/rentals/bookings/${id}/set_status/`, payload);
        return data;
    },

    cancelBooking: async (id: number | string, reason?: string) => {
        const { data } = await apiClient.post<RentalBooking>(`/rentals/bookings/${id}/cancel/`, { reason: reason || '' });
        return data;
    },

    extendBooking: async (id: number | string, payload: { end_date: string; vendor_notes?: string }) => {
        const { data } = await apiClient.post<RentalBooking>(`/rentals/bookings/${id}/extend/`, payload);
        return data;
    },

    addBookingAttachments: async (id: number | string, payload: FormData) => {
        const { data } = await apiClient.post(`/rentals/bookings/${id}/add_attachment/`, payload, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return data;
    },

    getFavorites: async () => {
        const { data } = await apiClient.get('/rentals/favorites/');
        return data;
    },

    addFavorite: async (rental_car: number) => {
        const { data } = await apiClient.post('/rentals/favorites/', { rental_car });
        return data;
    },

    removeFavorite: async (favoriteId: number | string) => {
        const { data } = await apiClient.delete(`/rentals/favorites/${favoriteId}/`);
        return data;
    },
};
